import React, { useEffect, useState } from 'react';
import Sidebar from '../components/Sidebar';
import DataTable from '../components/DataTable';
import ErrorBoundary from '../components/ErrorBoundary';
import { apiFetch } from '../api';
import { tableNameMap, columnNameMap } from '../labels';

export interface TableColumn {
  key: string;
  label: string;
  type?: 'text' | 'number' | 'date' | 'reference' | 'boolean';
  refTable?: string;
}

export interface TableSchema {
  name: string;
  columns: TableColumn[];
  label?: string;
}

const App: React.FC = () => {
  const [tables, setTables] = useState<TableSchema[]>([]);
  const [current, setCurrent] = useState<TableSchema | null>(null);
  const [isSchemaLoading, setIsSchemaLoading] = useState(true);
  const [schemaError, setSchemaError] = useState<string | null>(null);
  const roleLevel = (() => {
    const stored = localStorage.getItem('userRoleLevel');
    if (stored !== null && stored !== undefined && stored !== '') {
      const numeric = Number(stored);
      if (!Number.isNaN(numeric)) {
        return numeric;
      }
    }
    const role = (localStorage.getItem('userRole') || '').toLowerCase();
    if (role === 'owner') return 15;
    if (role === 'admin') return 10;
    if (role === 'manager') return 5;
    if (role === 'user') return 1;
    const token = localStorage.getItem('token');
    if (token) {
      const [, payload] = token.split('.');
      if (payload) {
        try {
          const decoded = JSON.parse(atob(payload.replace(/-/g, '+').replace(/_/g, '/')));
          return Number(decoded.roleLevel || 0);
        } catch (error) {
          console.error(error);
        }
      }
    }
    return 0;
  })();
  const OWNER_LEVEL = 15;
  const ADMIN_LEVEL = 10;

  useEffect(() => {
    async function load() {
      setIsSchemaLoading(true);
      setSchemaError(null);
      try {
        const res = await apiFetch('/api/schema');

        const tableOrder = [
          'articles',
          'products',
          'plan_records',
          'finance_operations',
          'mappings',
          'users',
          'roles',
          'queries',
        ];
        const hiddenTables = new Set([
          'fact_records',
          'app_settings',
          'users',
        ]);
        const ownerOnlyTables = new Set([
          'migrations',
          'tenant_schema_migrations',
          'tenant_schema_migration',
          'delivery_sync_state',
        ]);
        let data = await res.json();
        data = data.map((t: TableSchema) => {
          let cols = [...t.columns];
          if (t.name === 'fact_records') {
            const extIdx = cols.findIndex(
              c => c.key === 'externalId' || c.key === 'external_id'
            );
            const commIdx = cols.findIndex(
              c => c.key === 'commissionRate' || c.key === 'commission_rate'
            );
            if (extIdx !== -1 && commIdx !== -1) {
              const [ext] = cols.splice(extIdx, 1);
              const newCommIdx = cols.findIndex(
                c => c.key === 'commissionRate' || c.key === 'commission_rate'
              );
              cols.splice(newCommIdx, 0, ext);
            }
            const totalIdx = cols.findIndex(
              c => c.key === 'totalCost' || c.key === 'total_cost'
            );
            const priceIdx = cols.findIndex(c => c.key === 'price');
            if (totalIdx !== -1 && priceIdx !== -1) {
              const [tot] = cols.splice(totalIdx, 1);
              const newPriceIdx = cols.findIndex(c => c.key === 'price');
              cols.splice(newPriceIdx + 1, 0, tot);
            }
          }
          return {
            ...t,
            label: tableNameMap[t.name] || t.name,
            columns: cols.map(c => ({
              ...c,
              label: columnNameMap[t.name]?.[c.key] || c.label,
            })),
          };
        });
        data = data.filter((t: TableSchema) => !hiddenTables.has(t.name));
        if (roleLevel < OWNER_LEVEL) {
          data = data.filter((t: TableSchema) => !ownerOnlyTables.has(t.name));
        }
        if (roleLevel < ADMIN_LEVEL) {
          data = [];
        }
        data.sort((a: TableSchema, b: TableSchema) => {
          const aIndex = tableOrder.indexOf(a.name);
          const bIndex = tableOrder.indexOf(b.name);
          if (aIndex === -1 && bIndex === -1) {
            return (a.label || a.name).localeCompare(b.label || b.name, 'ru');
          }
          if (aIndex === -1) return 1;
          if (bIndex === -1) return -1;
          return aIndex - bIndex;
        });
        setTables(data);
        setCurrent(data[0] ?? null);
      } catch (error) {
        setSchemaError('Не удалось загрузить список таблиц');
        console.error(error);
      } finally {
        setIsSchemaLoading(false);
      }
    }
    load();
  }, []);

  return (
    <div style={{ display: 'flex', minHeight: '100vh' }}>
      <div className="absolute top-2 right-2 z-10"></div>
      <Sidebar tables={tables} current={current} onSelect={setCurrent} />
      <div className="content">
        {isSchemaLoading ? (
          <div className="flex min-h-[60vh] items-center justify-center">
            <div className="flex items-center gap-3 text-sm text-slate-500">
              <span className="h-5 w-5 animate-spin rounded-full border-2 border-slate-200 border-t-indigo-600"></span>
              Загружаем таблицы...
            </div>
          </div>
        ) : schemaError ? (
          <div className="py-6 text-center text-sm text-slate-500">{schemaError}</div>
        ) : current ? (
          <ErrorBoundary key={current.name}>
            <DataTable schema={current} />
          </ErrorBoundary>
        ) : null}
      </div>
    </div>
  );
};

export default App;
